import React, { useEffect, useState } from 'react';
import { Table, Button, DOMHelper, Stack, IconButton, Whisper, Tooltip, toaster, Message, Divider, SortType, Tag, Panel, Heading, Input, SelectPicker } from 'rsuite';
import { BaseErrorModel, BaseResponseModel, PagedResponseModel, PaginationFilterResult, DateTimeFormatOptions } from '@/models/core.model';
import { AxiosError } from 'axios';
import { toastErrorMessage, toastSuccessMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import { RunningClubAlbumImage, ClubAlbumImagePaginationFilterRequest, ClubAlbumImageStatusList } from '@/models/running-club-album-image.model';
import { deleteRunningClubAlbumImage, getPagedRunningClubAlbumImages } from '@/services/running-club-album.service';
import { Icon } from '@rsuite/icons';
import { MdDelete, MdArrowBack } from 'react-icons/md';
import ImageCell from '@/components/ImageCell';
import PageContent from '@/components/PageContent';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

export interface RunningClubAlbumImagesProps {
  albumId: number;
  albumName?: string;
  onBack?: () => void;
}

const statusColor = (status: number) => {
  switch (status) {
    case 2: return 'green';
    case 3: return 'red';
    default: return 'orange';
  }
};

const RunningClubAlbumImages: React.FC<RunningClubAlbumImagesProps> = ({ albumId, albumName, onBack }) => {
  const [filterReq, setFilterReq] = useState<ClubAlbumImagePaginationFilterRequest>(new ClubAlbumImagePaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<RunningClubAlbumImage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq, albumId]);

  const fetchData = async (req: ClubAlbumImagePaginationFilterRequest) => {
    setLoading(true);
    try {
      const result: PagedResponseModel<RunningClubAlbumImage> = await getPagedRunningClubAlbumImages(albumId, req);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({
          ...pre,
          pageNumber: result.pageNumber,
          totalRecords: result.totalRecords,
          pageSize: result.pageSize
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching Running Club Album Images:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre,
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  const onDeleteItem = (id: number) => {

    const handleYesClick = async () => {
      setLoading(true);
      try {
        const result: BaseResponseModel<null> = await deleteRunningClubAlbumImage(id);
        if (result.success) {
          toastSuccessMessage(`Album Image Removed.`);
          fetchData(filterReq);
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in deleting Album Image:${errorModel?.errorMessage}`);
      }
      finally {
        setLoading(false);
        toaster.clear();
      }
    };

    const handleNoClick = () => {
      toaster.clear();
    };

    toaster.push(
      <Message type="error" closable>
        <p style={{ marginBottom: 10 }}>Are you sure you want to remove this <strong>Image</strong>?</p>
        <div>
          <Button appearance="primary" color='red' onClick={handleYesClick}>Yes</Button>
          <Button appearance="subtle" onClick={handleNoClick} style={{ marginLeft: 10 }}>No</Button>
        </div>
      </Message>,
      { placement: 'topCenter', duration: 0 }
    );

  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Heading>{albumName}</Heading>
        <Stack spacing={6}>
          <SelectPicker
            data={ClubAlbumImageStatusList}
            placeholder="Status"
            searchable={false}
            onChange={value => setFilterReq(pre => ({...pre, approvalStatus: value ?? undefined}))}
          />
          {onBack && (
            <IconButton icon={<Icon as={MdArrowBack} />} onClick={onBack} block>
              Back to Albums
            </IconButton>
          )}
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
        rowHeight={80}
      >
        <Column flexGrow={1} align="center">
          <HeaderCell>Image</HeaderCell>
          <ImageCell dataKey="link" />
        </Column>
        <Column flexGrow={1} align="left" verticalAlign="middle">
          <HeaderCell>Uploader</HeaderCell>
          <Cell>
            {(rowData: RunningClubAlbumImage) => rowData.uploader?.name ?? '—'}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center" verticalAlign="middle">
          <HeaderCell>Approval Status</HeaderCell>
          <Cell>
            {(rowData: RunningClubAlbumImage) => (
              <Tag color={statusColor(rowData.approvalStatus)}>
                {ClubAlbumImageStatusList.find(s => s.value === rowData.approvalStatus)?.label ?? rowData.approvalStatus}
              </Tag>
            )}
          </Cell>
        </Column>
        <Column flexGrow={1} align="left" verticalAlign="middle">
          <HeaderCell>Approved By</HeaderCell>
          <Cell>
            {(rowData: RunningClubAlbumImage) => rowData.approvedByUser?.name ?? '—'}
          </Cell>
        </Column>
        <Column flexGrow={1} align="left" verticalAlign="middle" sortable>
          <HeaderCell>Created</HeaderCell>
          <Cell>
            {(rowData: RunningClubAlbumImage) => rowData.createdAt ? new Date(rowData.createdAt).toLocaleString(undefined, DateTimeFormatOptions) : ''}
          </Cell>
        </Column>
        <Column flexGrow={1} align="left" verticalAlign="middle" fixed="right">
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {(rowData: RunningClubAlbumImage) => (
              <Stack spacing={16}>
                <Whisper placement="top" trigger="hover" speaker={<Tooltip>Delete Image</Tooltip>}>
                  <IconButton circle size="sm" color="red" appearance="ghost"
                    onClick={() => onDeleteItem(rowData.id)}
                    icon={<Icon as={MdDelete} />} />
                </Whisper>
              </Stack>
            )}
          </Cell>
        </Column>

      </Table>
      <Divider />
      <TablePagination
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({ ...pre, pageNumber: dataKey }))}
        onChangeLimit={dataKey => setFilterReq(pre => ({ ...pre, pageSize: dataKey }))}
      />
    </PageContent>
  );
};

export default RunningClubAlbumImages;
